import { ref, readonly, type DeepReadonly, type Ref } from 'vue';

export interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
  duration: number;
}

const toasts = ref<Toast[]>([]);
let nextId = 0;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

interface UseToastReturn {
  toasts: DeepReadonly<Ref<Toast[]>>
  showSuccess: (message: string) => void
  showError: (message: string) => void
  dismiss: (id: number) => void
}

export function useToast(): UseToastReturn {
  function addToast(type: 'success' | 'error', message: string) {
    const duration = type === 'success' ? 5000 : 8000;
    const id = nextId++;
    toasts.value.push({ id, type, message, duration });
    const timer = setTimeout(() => dismiss(id), duration);
    timers.set(id, timer);
  }

  function showSuccess(message: string) {
    addToast('success', message);
  }

  function showError(message: string) {
    addToast('error', message);
  }

  function dismiss(id: number) {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
    toasts.value = toasts.value.filter(t => t.id !== id);
  }

  return { toasts: readonly(toasts), showSuccess, showError, dismiss };
}
